import { Button } from '@finn/design-system/atoms/button';
import { Skeleton } from '@finn/design-system/atoms/skeleton';
import { useSubscriptionPrice } from '@finn/ua-vehicle';
import { getDefaultTerm } from '@finn/ua-vehicle/ssr';
import { cn, useIntl } from '@finn/ui-utils';
import { getCloudinaryImgUrl } from '@finn/ui-utils/lib/cloudinary';
import { MouseEvent, useCallback } from 'react';

import { GenericVehicleDetails } from '../../../core';
import { PriceInformation } from '../../../price/PriceInformation';
import { Label } from '../../../product-cards/components/Label';
import { getPowerLabel } from '../../../product-cards/helpers/card-utils';
import { useDeliveryText } from '../../hooks/useDeliveryText';
import { Differences } from './Differences';
import { SwapInfo } from './SwapInfo';
import { VehicleColors } from './VehicleColors';

export const MobileGroupedProduct = ({
  idx,
  product,
  closedUserGroupId,
  handleOpenPCP,
  handleOnColorChange,
  handleMoreInformationClick,
  handleImageClick,
  isLoading,
  selectedConfig,
  isPDPHelperEnabled,
  showLabel,
  showDiff,
}: {
  idx: number;
  product: GenericVehicleDetails;
  closedUserGroupId?: string;
  handleOpenPCP: (product: GenericVehicleDetails) => void;
  handleOnColorChange: (configId: number) => void;
  handleMoreInformationClick: (product: GenericVehicleDetails) => void;
  handleImageClick: (product: GenericVehicleDetails) => void;
  isLoading?: boolean;
  selectedConfig?: number;
  isPDPHelperEnabled?: boolean;
  showLabel?: boolean;
  showDiff?: boolean;
}) => {
  const i18n = useIntl();
  const term = getDefaultTerm(product);
  const power = getPowerLabel(i18n, product.power);

  const { originalPriceFormatted, finalPriceFormatted } = useSubscriptionPrice({
    vehicle: product,
    closedUserGroupId,
  });

  const deliveryText = useDeliveryText(product, term, true);
  const vehiclePictureURL = getCloudinaryImgUrl(product.picture.url, {
    w: 95,
    c: 'fill',
    dpr: 2,
    fl: 'progressive',
  });
  const shouldHighlight = selectedConfig === product.config_id;

  const onImageClick = useCallback(() => {
    handleImageClick(product);
    handleMoreInformationClick(product);
  }, [handleImageClick, handleMoreInformationClick, product]);

  return (
    <div
      id={`mobile-${product.config_id}`}
      data-idx={idx}
      key={product.id}
      className={cn(
        'relative border-b border-r border-pearl px-4 py-6',
        shouldHighlight && 'animate-outline-pulse'
      )}
    >
      {isLoading ? (
        <Skeleton className="absolute right-2 top-4 h-20 w-[136px]" />
      ) : (
        <div className="absolute right-2 top-4" onClick={onImageClick}>
          <img
            src={vehiclePictureURL}
            alt={product.equipment_line}
            className="w-[136px] mix-blend-multiply"
          />
          <img
            className="absolute bottom-2 right-2"
            alt="camera icon"
            src="https://imgix.cosmicjs.com/9b4d7f40-5cc7-11f0-8c29-1f1774a71ae1-camera-with-strokes.png"
          />
        </div>
      )}

      {showLabel && product.product_label.label && (
        <Label highlight className="mb-3">
          {product.product_label.label}
        </Label>
      )}
      <div
        className="mb-3 flex pr-28 underline body-14-semibold"
        onClick={() => handleMoreInformationClick(product)}
      >
        {product.equipment_line}
      </div>

      <VehicleColors
        product={product}
        onColorChange={handleOnColorChange}
        isMobile
      />
      <div className="mt-3 body-14-regular">
        <SwapInfo product={product}>Verfügbarkeit {deliveryText}</SwapInfo>
      </div>
      <div className="mt-4 flex flex-row flex-wrap items-center gap-2">
        <span className="body-14-regular">{product.fuel}</span>
        <span className="size-0.5 rounded bg-pewter" />
        <span className="body-14-regular">{power}</span>
        <span className="size-0.5 rounded bg-pewter" />
        <span className="body-14-regular">{product.config_drive}</span>
      </div>
      {showDiff && (
        <Differences
          configId={product.config_id}
          className="mt-6"
          isFirst={idx === 0}
        />
      )}
      <div className="mb-4 mt-6">
        {isLoading ? (
          <Skeleton className="h-8 w-24" />
        ) : (
          <span className="flex flex-row items-center global-t5-semibold">
            {originalPriceFormatted && (
              <span className="mr-2 inline-block !text-iron line-through">
                {originalPriceFormatted}
              </span>
            )}
            <span>ab&nbsp;</span>
            <span>{finalPriceFormatted}</span>
            <span className="ml-2 body-14-regular">pro Monat</span>
            <PriceInformation size="small" />
          </span>
        )}
      </div>
      <Button
        onClick={(e: MouseEvent<HTMLButtonElement>) => {
          e.preventDefault();
          e.stopPropagation();
          handleOpenPCP(product);
        }}
        size="lg"
        className="w-full"
      >
        Abo konfigurieren
      </Button>
      {isPDPHelperEnabled && (
        <Button
          variant="link"
          className="mt-2 w-full border border-solid p-2"
          size="sm"
          href={`/pdp/${product.id}`}
          target="_blank"
        >
          Open PDP page
        </Button>
      )}
    </div>
  );
};
